import { parseFolderId, isVirtualFolder } from "./folderIdUtils";
import {
  CANVAS_ROUTE,
  CANVAS_VIEW_BASE_PATH,
  INTERFACE_ROUTE,
  TABLE_ROUTE,
  virtualFoldersById,
  CHATBOT_ROUTE,
  DEFAULT_FOLDER_ID,
  MONOLITH_BASE_URL,
} from "./constants";
import { isProduction } from "./environment";

// *****************************************************************************
// Asset Index URLs
// *****************************************************************************

export const getFoldersIndexUrl = () => "/app/assets/zaps/folders";
export const getChatbotsIndexUrl = () => "/app/assets/chatbots";
export const getAgentsIndexUrl = () => "/app/assets/agents";
export const getZapsIndexUrl = () => "/app/assets/zaps";
export const getNextGenZapsIndexUrl = () => "/app/assets/next-gen-zaps";
export const getTablesIndexUrl = () => "/app/assets/tables";
export const getInterfacesIndexUrl = () => "/app/assets/forms";
// Next Gen Forms live in formsui, served on this app's host under /app/forms.
// There is deliberately no index URL: this app owns the forms list, and
// formsui's own /app/forms is going away.
export const getNextGenFormEditorUrl = (formId: string) =>
  `/app/forms/${formId}/edit`;
export const getTemplatesIndexUrl = () => "/app/assets/templates";
export const getAppConnectionsIndexUrl = () => "/app/assets/connections";
export const getAppsIndexUrl = () => "/app/assets/connections?view=apps";
export const getSdkCredentialsIndexUrl = () =>
  "/app/assets/connections/sdk-credentials";
export const getAssetsIndexUrl = () => "/app/assets";

// *****************************************************************************
// Folders
// *****************************************************************************

export const getVirtualFolderUrl = (
  folderId: string,
  memberId?: number,
): string => {
  if (virtualFoldersById[folderId]) {
    const { href } = virtualFoldersById[folderId];
    if (memberId) {
      return `${href}${href.includes("?") ? "&" : "?"}memberId=${memberId}`;
    }
    return href;
  }

  return "/app/assets/zaps";
};

export const getFolderUrl = (id?: string | null, memberId?: number) => {
  if (id === "trash") {
    return "/app/assets/zaps/folders/trash";
  }

  // if no id is passed in, then the folderId is null, and we should return the personal folder
  // also check for string "0"
  if (!id || id === DEFAULT_FOLDER_ID) {
    if (memberId) {
      return `/app/assets/zaps/folders/personal?memberId=${memberId}`;
    }

    return `/app/assets/zaps/folders/personal`;
  }

  const folderId = parseFolderId(id);
  if (isVirtualFolder(folderId)) {
    return getVirtualFolderUrl(folderId, memberId);
  }
  if (memberId) {
    return `/app/assets/folders/${id}?memberId=${memberId}`;
  }
  return `/app/assets/folders/${id}`;
};

// *****************************************************************************
// Projects
// *****************************************************************************

export const getProjectUrl = (projectId?: string | null) =>
  projectId ? `/app/projects/${projectId}` : "";

// *****************************************************************************
// Zaps
// *****************************************************************************

export const getEditZapUrl = (id: string) => `/webintent/edit-zap/${id}`;

export const getEditorStepUrl = (
  rootStepId: string,
  stepId: string,
  panel: "setup" | "fields" = "setup",
) =>
  `/editor/${rootStepId.replace(/^series_/, "")}/published/${stepId}/${panel}`;

// `referring_source` value is fixed by the editor's allowlist; drift silently no-ops the wizard.
export const getCreateTemplateFromZapUrl = (
  zapId: string,
  mode: "published" | "draft",
) => `/editor/${zapId}/${mode}?referring_source=guided_templates_assets`;

const LAST_RUN_DAYS: Record<string, number> = {
  within_1: 1,
  within_7: 7,
  within_30: 30,
  within_60: 60,
};

export function getLastRunDateRange(lastRun: string | undefined) {
  if (!lastRun) return;
  const days = LAST_RUN_DAYS[lastRun];
  if (days === undefined) return;
  const now = new Date();
  const from = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
  return {
    start_datetime: from.toISOString().slice(0, 10),
    end_datetime: now.toISOString().slice(0, 10),
  };
}

export const getZapHistoryUrl = (
  id: string,
  dateRange?: { start_datetime: string; end_datetime: string },
) => {
  const searchParameters = new URLSearchParams({ root_id: id });
  if (dateRange) {
    searchParameters.set("start_datetime", dateRange.start_datetime);
    searchParameters.set("end_datetime", dateRange.end_datetime);
  }
  return `/app/history?${searchParameters.toString()}`;
};

export const getCreateZapUrl = (folderId?: string) =>
  `/webintent/create-zap?entry-point-method=make_a_zap_call_to_action&entry-point-location=my_zaps${
    folderId && !isVirtualFolder(folderId) ? `&folder_id=${folderId}` : ""
  }`;
export const getZApprovalsUrl = () => "/app/assets/zaps/approvals";

export const getRunDetailsUrl = (zapId: string, runId: string) =>
  `/editor/${zapId}/run/${runId}/run-details?sidebar=runs&filterRun=true`;

export const getZapsByConnectionUrl = (
  selectedApi: string,
  slug: string,
  connectionId: number,
) => {
  if (!selectedApi || !connectionId) return "";

  if (!slug) {
    return `/app/assets/zaps?app=${encodeURIComponent(`${selectedApi}`)}&connection=${connectionId}`;
  }

  return `/app/assets/zaps?app=${encodeURIComponent(`${selectedApi}::${slug}`)}&connection=${connectionId}`;
};

export const getZapsByConnectionIdUrl = (connectionId: number) => {
  if (!connectionId) return "";
  return `/app/assets/zaps?connection=${connectionId}`;
};

export const getZapsHrefForConnection = (
  connectionId?: number | null,
): string | undefined => {
  if (!connectionId) return;
  return getZapsByConnectionIdUrl(connectionId);
};
export const getZapsByAppUrl = (selectedApi: string, slug?: string) => {
  if (!!selectedApi && !!slug) {
    return `/app/assets/zaps?app=${encodeURIComponent(`${selectedApi}::${slug}`)}`;
  }

  if (selectedApi) {
    return `/app/assets/zaps?app=${encodeURIComponent(`${selectedApi}`)}`;
  }

  return "/app/assets/zaps";
};

// *****************************************************************************
// Connections / Authentications
// *****************************************************************************

export const getAppDetailsUrl = (slug: string) =>
  `/app/assets/connections/${slug}`;

// *****************************************************************************
// Templates
// *****************************************************************************

export const getAppTemplatesUrl = (slug: string) =>
  slug ? `/app/templates/apps?apps=${slug}` : "/app/templates/apps";

export interface GetUseTemplateUrlOptions {
  /** Referrer URL for logging (will be encoded when added to query string) */
  referrerUrl?: string;
}

/**
 * URL for the "Use Template" flow: opens the create-system webintent which
 * creates a zap from the template and redirects to the editor.
 * Requires source_template_slug (template slug); optionally pass referrer_url for logging.
 * Uses absolute URL when MONOLITH_BASE_URL (Zapier origin) is set so that in local dev
 * the link targets the Zapier app (e.g. staging) instead of the assetmanagement proxy.
 */
export const getUseTemplateUrl = (
  templateSlug: string,
  options?: GetUseTemplateUrlOptions,
): string => {
  const parameters = new URLSearchParams({
    source_template_slug: templateSlug,
  });
  if (options?.referrerUrl) {
    parameters.set("referrer_url", options.referrerUrl);
  }
  const path = `/webintent/create-system?${parameters.toString()}`;
  const base = MONOLITH_BASE_URL?.replace(/\/$/, "");
  return base ? `${base}${path}` : path;
};

/**
 * URL for the "Edit Template" flow: navigates cross-app to the editor's
 * dedicated edit-template route, which resolves the source Zap server-side
 * and opens the template in edit mode.
 * Uses absolute URL when MONOLITH_BASE_URL is set so that in local dev
 * the link targets the Zapier app (e.g. staging) instead of the assetmanagement proxy.
 */
export const getEditTemplateUrl = (templateId: string): string => {
  const parameters = new URLSearchParams({
    referring_source: "guided_templates_assets",
  });
  const path = `/editor/edit-template/${templateId}?${parameters.toString()}`;
  const base = MONOLITH_BASE_URL?.replace(/\/$/, "");
  return base ? `${base}${path}` : path;
};

export const getConnectionsByAppUrl = (selectedApi: string) => {
  return `/app/assets/connections?app=${encodeURIComponent(`${selectedApi}`)}`;
};

// *****************************************************************************
// Tables
// *****************************************************************************

export const getTablesListUrl = () => "/app/assets/tables";

export interface TableUrlOptions {
  /** A secret token used to access a table via a public link */
  secret?: string;
}

export const getTableBuilderUrl = (
  tableId: string,
  options?: TableUrlOptions,
) => {
  const url = new URL(`${TABLE_ROUTE}/t/${tableId}`);
  if (options?.secret) {
    url.searchParams.set("secrets", options.secret);
  }
  return url.href;
};

// *****************************************************************************
// Interfaces
// *****************************************************************************

export const getInterfacesListUrl = () => "/app/assets/forms";
export const getInterfaceBuilderUrl = (interfaceId: string) =>
  `${INTERFACE_ROUTE}/${interfaceId}`;
export const getInterfacePublishedUrl = (slug: string) => {
  const domain = isProduction ? "zapier.app" : "staging.zapier.app";
  return `https://${slug}.${domain}/`;
};

// *****************************************************************************
// Canvases
// *****************************************************************************
export const getCanvasesListUrl = () => "/app/assets/canvases";

export const getCanvasBuilderUrl = (canvasId: string) =>
  `${CANVAS_ROUTE}${canvasId}`;

export const getCanvasPublicUrl = (canvasId: string) =>
  `${CANVAS_VIEW_BASE_PATH}/${canvasId}`;

// *****************************************************************************
// Chatbot
// *****************************************************************************

export const getChatbotBuilderUrl = (chatbotId: string) =>
  `${CHATBOT_ROUTE}${chatbotId}`;
